import React, { useEffect, useRef, useState } from 'react';
import './Settings.scss';
import ExplainTips from '../../utils/ExplainTips';
import AudioLevelMeter from './AudioLevelMeter';
import { IOSSwitch } from '../../Switches/Switches';

import Select from 'react-select';
import * as R from 'ramda';

import { WebRTCsdkService } from '../../../services/webrtcsdk-service';
import { SoundService } from '../../../services/sound-service';
import { container, singleton } from "tsyringe";

import { ReactComponent as AudioIcon } from '../../../assets/icons/icon-settings-audio.svg';
import { ReactComponent as IconSpeaker } from '../../../assets/icons/icon-speaker.svg';
import { useSharedState } from '../../../utils/helpers';
import { SettingService, SettingsKey } from '../../../services/setting-service';

import { Slider, styled } from '@mui/material';
import { useNotTooFast } from '../../hooks/useNotTooFast';



const GainSlider = styled(Slider)({
  color: '#96b0a3',
  height: 5,
  '& .MuiSlider-track': {
    border: 'none',
    backgroundImage: 'linear-gradient(to right, #00e07c 0px, #ffbb5e 180px, #ff4c4c)',
  },
  '& .MuiSlider-thumb': {
    height: 25,
    width: 25,
    backgroundColor: '#fff',
    marginTop: 0,
    marginLeft: 0,
    border: '2px solid #4a4a4a',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&:before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    lineHeight: 1.2,
    fontSize: 12,
    background: 'unset',
    padding: 0,
    width: 32,
    height: 32,
    borderRadius: '50% 50% 50% 0',
    backgroundColor: '#52af77',
    transformOrigin: 'bottom left',
    transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
    '&:before': { display: 'none' },
    '&.MuiSlider-valueLabelOpen': {
      transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
    },
  },
  '& .MuiSlider-mark': {
    backgroundColor: '#cccccc',
    height: 0,
    width: 1,
    marginTop: 5,
    '&.MuiSlider-markActive': {
      opacity: 1,
      backgroundColor: 'currentColor',
    },
  },
});


const SettingsAudio = () => {

  // const settings = useSelector((state: any) => state.settings);

  const [audioTimer, setAudioTimer] = useState(null);

  const [webRTCsdkService] = useState(container.resolve(WebRTCsdkService));
  const [settingService] = useState(container.resolve(SettingService));
  const [soundService] = useState(container.resolve(SoundService));
  const [localMicrophones] = useSharedState(webRTCsdkService.localMicrophones$);
  const [localSpeakers] = useSharedState(webRTCsdkService.localSpeakers$);

  const [selectedSpeaker] = useSharedState(webRTCsdkService.selectedSpeaker$);

  const [selectedMic, setSelectedMic] = useState(null);
  const [micOptions, setMicOptions] = useState([]);

  const [selectedSpk, setSelectedSpk] = useState(null);
  const [speakerOptions, setSpeakerOptions] = useState([]);
  const [soundTesting, setSoundTesting] = useState(false);

  const [settings, setSettings] = useState(
    {
      echoCancellation: settingService.getSetting(SettingsKey.echoCancellation),
      stereoInput: settingService.getSetting(SettingsKey.stereoInput),
      autoGain: settingService.getSetting(SettingsKey.autoGain),
      manualGain: settingService.getSetting(SettingsKey.manualGain),
      manualGainDefault: settingService.getSetting(SettingsKey.manualGain),
    }
  )

  const checkNotTooFast = useNotTooFast(400);

  useEffect(() => {
    console.log('MICROPHONES', localMicrophones);
    const options = [];
    if (localMicrophones.length > 0) {
      localMicrophones.forEach(m => options.push({ value: m.id, label: m.name }));
      setMicOptions(options);

      // check if one of the microphone match last used
      let index = 0;
      const lastUsedDevices = JSON.parse(localStorage.getItem("lastUsedDevices") || "{}");
      if (lastUsedDevices && lastUsedDevices.microphone) {
        index = localMicrophones.findIndex(m => m.name === lastUsedDevices.microphone.name);
      }
      if (index < 0) index = 0;
      setSelectedMic(options[index]);
      webRTCsdkService.setSelectedMicrophone(options[index].value);
    }
  }, [localMicrophones]);

  useEffect(() => {
    console.log('SPEAKERS', localSpeakers);
    const options = [];
    if (localSpeakers.length > 0) {
      localSpeakers.forEach(s => options.push({ value: s.id, label: s.name }));
      setSpeakerOptions(options);

      // check if one of the speakers match last used
      let index = 0;
      const lastUsedDevices = JSON.parse(localStorage.getItem("lastUsedDevices") || "{}");
      if (lastUsedDevices && lastUsedDevices.speaker) {
        index = localSpeakers.findIndex(s => s.name === lastUsedDevices.speaker.name);
      }
      if (index < 0) index = 0;
      setSelectedSpk(options[index]);
      webRTCsdkService.setSelectedSpeaker(options[index].value);
    }
  }, [localSpeakers]);

  const onToggleEcho = () => {
    if (checkNotTooFast()) {
      const value = !settings.echoCancellation;
      // toggle echoCancellation in settings
      setSettings({ ...settings, echoCancellation: value });
      settingService.setSetting(SettingsKey.echoCancellation, value);
    }
  }
  const onToggleStereo = () => {
    if (checkNotTooFast()) {
      // toggle stereoInput in settings
      const value = !settings.stereoInput;
      setSettings({ ...settings, stereoInput: value });
      settingService.setSetting(SettingsKey.stereoInput, value);
    }
  }
  const onToggleGain = () => {
    if (checkNotTooFast()) {
      // toggle autoGain in settings
      const value = !settings.autoGain;
      setSettings({ ...settings, autoGain: value });
      settingService.setSetting(SettingsKey.autoGain, value);
    }
  }

  const onChangeManualGain = (event, newValue) => {
    setSettings({ ...settings, manualGain: newValue });
    settingService.setSetting(SettingsKey.manualGain, newValue);
  }


  const onSoundTest = () => {
    if (audioTimer) {
      clearTimeout(audioTimer);
    }
    if (soundTesting) {
      setSoundTesting(false);
      soundService.testAudioStop();
      return;
    }
    setSoundTesting(true);
    soundService.testAudioStart(selectedSpeaker);
    setAudioTimer(setTimeout(() => {
      setSoundTesting(false);
      soundService.testAudioStop();
    }, 4000)); // SET duration of audio file
  }

  const onSelectMic = (value, action) => {
    console.log('mic', value, action);
    setSelectedMic(value);
    webRTCsdkService.setSelectedMicrophone(value.value);
  }
  const onSelectSpeaker = (value, action) => {
    console.log('speaker', value, action);
    setSelectedSpk(value);
    webRTCsdkService.setSelectedSpeaker(value.value);
  }

  return (
    <div className="settings-tab">
      <div className="title">
        <AudioIcon className="icon" />
        <div>Audio Settings</div>
      </div>
      <ul className="items">

        <li key="microphone">
          <div className="container">
            <div className="block">Microphone
            </div>
          </div>

          <div className="selector">
            <Select
              className="basic-single"
              classNamePrefix="select"
              value={selectedMic}
              name="microphone"
              options={micOptions}
              onChange={onSelectMic}
              isSearchable={false}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary25: '#f0f0f0',
                  primary50: '#4a4a4a',
                  primary: '#cbd2da',
                },
              })}
            />
          </div>
          <div className="mic-level">
            <AudioIcon className="icon-small" />
            <AudioLevelMeter></AudioLevelMeter>
          </div>
        </li>
        <li key="speaker">
          <div className="container">
            <div className="block">Speaker
            </div>
          </div>

          <div className="selector">
            <Select
              className="basic-single"
              classNamePrefix="select"
              value={selectedSpk}
              name="speaker"
              options={speakerOptions}
              onChange={onSelectSpeaker}
              isSearchable={false}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary25: '#f0f0f0',
                  primary50: '#4a4a4a',
                  primary: '#cbd2da',
                },
              })}
            />
          </div>

          <div className={"speaker-test " + (soundTesting ? 'speaker-testing' : '')} onClick={onSoundTest}>
            <IconSpeaker className="icon" />
            <div>{soundTesting ? 'Testing ...' : 'Test'}</div>
          </div>
        </li>

        <li key="echoCancellation">
          <div className="container">
            <div className="block">Echo Cancellation
              <ExplainTips text={'This setting enables audio processing features like echo cancellation, noise supression, and auto gain control. Podcasters and musicians often turn this off to get a more honest dynamic sound from the mic.'} tipStyle={{}}></ExplainTips>
            </div>
            <div className="block">
              <div>
                <IOSSwitch
                  className='switch'
                  checked={settings?.echoCancellation || false}
                  onChange={() => onToggleEcho()}
                />
              </div>
            </div>
          </div>
        </li>
        {/* <li key="stereoInput">
          <div className="container">
            <div className="block">Stereo Audio Input
              <ExplainTips text={'This is mostly used by musicians with a stereo audio input/mic interface. Echo cancellation must be off for stereo audio to work properly.'} tipStyle={{}}></ExplainTips>
            </div>
            <div className="block">
              <div>
                <IOSSwitch
                  className='switch'
                  checked={settings?.stereoInput || false}
                  onChange={() => onToggleStereo()}
                />
              </div>
            </div>
          </div>
        </li> */}

        {/* <li key="autoGain">
          <div className="container">
            <div className="block">Auto Gain Control
              <ExplainTips text={'Disable this setting to manually adjust your mic volume with a slider.'} tipStyle={{}}></ExplainTips>
            </div>
            <div className="block">
              <div>
                <IOSSwitch
                  className='switch'
                  checked={settings.autoGain}
                  onChange={() => onToggleGain()}
                />
              </div>
            </div>
          </div>

          {!settings.autoGain &&
            <div className="container gain">
              <IconSpeaker className="icon"/>
              <div className="block">
                <GainSlider
                  className="gain-range"
                  aria-label="Gain Range"
                  defaultValue={settings.manualGainDefault}
                  valueLabelDisplay="off"
                  step={0.1}
                  min={1}
                  max={10}
                  onChange={onChangeManualGain}
                />
              </div>
            </div>
          }


        </li> */}

      </ul>
    </div>
  )

}


export default SettingsAudio;